import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import PermissaoWrapper from '../../components/PermissaoWrapper';
import { PermissaoEnum } from '../../model/Usuario/Permissao';
import { TemplateComposicao } from '../../model/composicao/TemplateComposicao';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../utils/breadcrumbUtils';
import { TemplateComposicaoViewModel } from '../../viewmodel/composicao/TemplateComposicaoViewModel';
import CategoriaIcone from '../Processos/components/CategoriaIcone';
import HelpDrawerComposicoes from './HelpDrawerComposicoes';
import { BsQuestionCircle } from 'react-icons/bs';

const Composicoes: React.FC = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [templates, setTemplates] = useState<TemplateComposicao[]>([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('TODOS');
    const [filtroTipo, setFiltroTipo] = useState('TODOS');
    const [filtroStatus, setFiltroStatus] = useState('ATIVOS');
    const [showModal, setShowModal] = useState(false);
    const [showHelpDrawer, setShowHelpDrawer] = useState(false);
    const [modalAction, setModalAction] = useState<{ type: 'desativar' | 'ativar' | 'delete', id: string, nome: string } | null>(null);

    useEffect(() => {
        setBreadcrumbs(createBreadcrumbs.fromRoot([
            { path: '/composicoes', breadcrumb: 'Composições de Bancas' }
        ]));
    }, [setBreadcrumbs]);

    useEffect(() => {
        carregarTemplates();
    }, []);

    const carregarTemplates = async () => {
        try {
            setLoading(true);
            const data = await TemplateComposicaoViewModel.getAll();
            setTemplates(data);
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const openModal = (type: 'desativar' | 'ativar' | 'delete', id: string, nome: string) => {
        setModalAction({ type, id, nome });
        setShowModal(true);
    };

    const handleConfirm = async () => {
        if (!modalAction) return;
        
        try {
            switch (modalAction.type) {
                case 'desativar':
                    await TemplateComposicaoViewModel.desativar(modalAction.id);
                    toast.success('Template desativado com sucesso');
                    break;
                case 'ativar':
                    await TemplateComposicaoViewModel.ativar(modalAction.id);
                    toast.success('Template ativado com sucesso');
                    break;
                case 'delete':
                    await TemplateComposicaoViewModel.delete(modalAction.id);
                    toast.success('Template excluído com sucesso');
                    break;
            }
            carregarTemplates();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setShowModal(false);
            setModalAction(null);
        }
    };

    const templatesFiltrados = templates.filter(t => {
        const matchNome = t.nome.toLowerCase().includes(filtro.toLowerCase()) ||
            (t.descricao && t.descricao.toLowerCase().includes(filtro.toLowerCase()));
        const matchCategoria = filtroCategoria === 'TODOS' || t.categoria === filtroCategoria;
        const matchTipo = filtroTipo === 'TODOS' || t.tipoBanca.nome === filtroTipo;
        const matchStatus = filtroStatus === 'TODOS' ||
            (filtroStatus === 'ATIVOS' && t.ativo) ||
            (filtroStatus === 'INATIVOS' && !t.ativo);
        return matchNome && matchCategoria && matchTipo && matchStatus;
    });

    const templatesAgrupados = templatesFiltrados.reduce((grupos, template) => {
        const chave = `${template.nome}-${template.categoria}`;
        if (!grupos[chave]) {
            grupos[chave] = {
                nome: template.nome,
                categoria: template.categoria,
                templates: []
            };
        }
        grupos[chave].templates.push(template);
        return grupos;
    }, {} as Record<string, { nome: string; categoria: string; templates: TemplateComposicao[] }>);



    const categorias = Array.from(new Set(templates.map(t => t.categoria)));
    const tiposBanca = Array.from(new Set(templates.map(t => t.tipoBanca.nome)));

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <>
            <HelpDrawerComposicoes isOpen={showHelpDrawer} onClose={() => setShowHelpDrawer(false)}>
            <div className="min-vh-100" style={{ backgroundColor: '#fafafa' }}>
                <Container fluid className="py-5">
                    <div className="mb-5">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h1 className="fw-light mb-2" style={{ fontSize: '2rem', color: '#1a1a1a' }}>Composições</h1>
                                <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>Gerencie os formatos de composição de bancas</p>
                            </div>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setShowHelpDrawer(true)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                                >
                                    <BsQuestionCircle /> Ajuda
                                </button>
                                <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_COMPOSICAO]}>
                                    <Button
                                        className="border-0 px-4 py-2"
                                        style={{ 
                                            backgroundColor: '#2563eb', 
                                            borderRadius: '8px',
                                            fontSize: '0.9rem',
                                            fontWeight: '500'
                                        }}
                                        onClick={() => navigate('/composicoes/novo')}
                                    >
                                        <i className="bi bi-plus me-2"></i>
                                        Novo Template
                                    </Button>
                                </PermissaoWrapper>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-3 p-4 mb-4" style={{ border: '1px solid #e5e7eb' }}>
                        <Row className="g-3">
                            <Col md={4}>
                                <Form.Control
                                    type="text"
                                    placeholder="Buscar por nome ou descrição..."
                                    value={filtro}
                                    onChange={(e) => setFiltro(e.target.value)}
                                    className="border-0"
                                    style={{ 
                                        backgroundColor: '#f8fafc', 
                                        borderRadius: '8px',
                                        padding: '12px 16px'
                                    }}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Select 
                                    value={filtroCategoria} 
                                    onChange={(e) => setFiltroCategoria(e.target.value)}
                                    className="border-0"
                                    style={{ 
                                        backgroundColor: '#f8fafc', 
                                        borderRadius: '8px',
                                        padding: '12px 16px'
                                    }}
                                >
                                    <option value="TODOS">Todas categorias</option>
                                    {categorias.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Select 
                                    value={filtroTipo} 
                                    onChange={(e) => setFiltroTipo(e.target.value)}
                                    className="border-0"
                                    style={{ 
                                        backgroundColor: '#f8fafc', 
                                        borderRadius: '8px',
                                        padding: '12px 16px'
                                    }}
                                >
                                    <option value="TODOS">Todos os tipos</option>
                                    {tiposBanca.map(tipo => (
                                        <option key={tipo} value={tipo}>{tipo}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Select 
                                    value={filtroStatus} 
                                    onChange={(e) => setFiltroStatus(e.target.value)}
                                    className="border-0"
                                    style={{ 
                                        backgroundColor: '#f8fafc', 
                                        borderRadius: '8px',
                                        padding: '12px 16px'
                                    }}
                                >
                                    <option value="TODOS">Todos status</option>
                                    <option value="ATIVOS">Ativos</option>
                                    <option value="INATIVOS">Inativos</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    </div>

                    <div className="bg-white rounded-3" style={{ border: '1px solid #e5e7eb' }}>
                        {templatesFiltrados.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="mb-3" style={{ color: '#9ca3af', fontSize: '3rem' }}>
                                    <i className="bi bi-inbox"></i>
                                </div>
                                <p className="text-muted mb-0">Nenhum template encontrado</p>
                            </div>
                        ) : (
                            <div className="p-4">
                                {Object.values(templatesAgrupados).map(grupo => (
                                    <div key={`${grupo.nome}-${grupo.categoria}`} className="mb-5">
                                        <div className="d-flex align-items-center mb-4 pb-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <h4 className="mb-0 me-3 fw-medium" style={{ color: '#1e293b' }}>{grupo.nome}</h4>
                                            <span 
                                                className="px-3 py-1 rounded-pill" 
                                                style={{ 
                                                    backgroundColor: '#eff6ff', 
                                                    color: '#1d4ed8',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                {grupo.categoria}
                                            </span>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-borderless">
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                                        <th className="fw-medium py-3" style={{ color: '#64748b', fontSize: '0.9rem' }}>Tipo</th>
                                                        <th className="fw-medium py-3" style={{ color: '#64748b', fontSize: '0.9rem' }}>Vínculos</th>
                                                        <th className="fw-medium py-3" style={{ color: '#64748b', fontSize: '0.9rem' }}>Status</th>
                                                        <th className="fw-medium py-3 text-end" style={{ color: '#64748b', fontSize: '0.9rem' }}>Ações</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {grupo.templates.map(template => (
                                                        <tr key={template.id} className="border-0">
                                                            <td className="py-3" style={{ color: '#374151', fontWeight: '500' }}>
                                                                {template.tipoBanca.nome}
                                                            </td>
                                                            <td className="py-3">
                                                                {template.regrasVinculos && template.regrasVinculos.length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {template.regrasVinculos.map(regra => (
                                                                            <span 
                                                                                key={regra.id}
                                                                                className="px-2 py-1 rounded"
                                                                                style={{
                                                                                    backgroundColor: '#f8fafc',
                                                                                    color: '#475569',
                                                                                    fontSize: '0.8rem',
                                                                                    border: '1px solid #e2e8f0'
                                                                                }}
                                                                                title={`${regra.vinculo.nome}: ${regra.minimo}-${regra.maximo}${regra.obrigatorio ? ' (obrigatório)' : ''}`}
                                                                            >
                                                                                {regra.vinculo.nome}: {regra.minimo}-{regra.maximo}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: '#9ca3af' }}>—</span>
                                                                )}
                                                            </td>
                                                            <td className="py-3">
                                                                <span 
                                                                    className="px-3 py-1 rounded-pill"
                                                                    style={{
                                                                        backgroundColor: template.ativo ? '#dcfce7' : '#f1f5f9',
                                                                        color: template.ativo ? '#166534' : '#64748b',
                                                                        fontSize: '0.8rem',
                                                                        fontWeight: '500'
                                                                    }}
                                                                >
                                                                    {template.ativo ? 'Ativo' : 'Inativo'}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 text-end">
                                                                <div className="d-flex justify-content-end gap-2">
                                                                    <button
                                                                        className="btn p-2 border-0"
                                                                        style={{ 
                                                                            backgroundColor: '#f8fafc',
                                                                            color: '#475569',
                                                                            borderRadius: '6px',
                                                                            width: '36px',
                                                                            height: '36px'
                                                                        }}
                                                                        onClick={() => navigate(`/composicoes/${template.id}`)}
                                                                        title="Visualizar"
                                                                    >
                                                                        <i className="bi bi-eye" style={{ fontSize: '0.9rem' }}></i>
                                                                    </button>
                                                                    {template.ativo ? (
                                                                        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.DESATIVAR_COMPOSICAO]}>
                                                                            <button
                                                                                className="btn p-2 border-0"
                                                                                style={{ 
                                                                                    backgroundColor: '#fef3c7',
                                                                                    color: '#d97706',
                                                                                    borderRadius: '6px',
                                                                                    width: '36px',
                                                                                    height: '36px'
                                                                                }}
                                                                                onClick={() => openModal('desativar', template.id, template.nome)}
                                                                                title="Desativar"
                                                                            >
                                                                                <i className="bi bi-pause" style={{ fontSize: '0.9rem' }}></i>
                                                                            </button>
                                                                        </PermissaoWrapper>
                                                                    ) : (
                                                                        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ATIVAR_COMPOSICAO]}>
                                                                            <button
                                                                                className="btn p-2 border-0"
                                                                                style={{ 
                                                                                    backgroundColor: '#dcfce7',
                                                                                    color: '#16a34a',
                                                                                    borderRadius: '6px',
                                                                                    width: '36px',
                                                                                    height: '36px'
                                                                                }}
                                                                                onClick={() => openModal('ativar', template.id, template.nome)}
                                                                                title="Ativar"
                                                                            >
                                                                                <i className="bi bi-play" style={{ fontSize: '0.9rem' }}></i>
                                                                            </button>
                                                                        </PermissaoWrapper>
                                                                    )}
                                                                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.DELETAR_COMPOSICAO]}>
                                                                        <button
                                                                            className="btn p-2 border-0"
                                                                            style={{ 
                                                                                backgroundColor: '#fee2e2',
                                                                                color: '#dc2626',
                                                                                borderRadius: '6px',
                                                                                width: '36px',
                                                                                height: '36px'
                                                                            }}
                                                                            onClick={() => openModal('delete', template.id, template.nome)}
                                                                            title="Excluir"
                                                                        >
                                                                            <i className="bi bi-trash" style={{ fontSize: '0.9rem' }}></i>
                                                                        </button>
                                                                    </PermissaoWrapper>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Container>
            </div>
            </HelpDrawerComposicoes>

            <Modal 
                show={showModal} 
                onHide={() => setShowModal(false)}
                centered
                backdrop="static"
            >
                <div className="modal-content border-0" style={{ borderRadius: '12px' }}>
                    <div className="modal-body p-4">
                        <div className="text-center mb-4">
                            <div 
                                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                                style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '50%',
                                    backgroundColor: modalAction?.type === 'delete' ? '#fee2e2' : '#fef3c7'
                                }}
                            >
                                <i 
                                    className={`bi ${modalAction?.type === 'delete' ? 'bi-exclamation-triangle' : 'bi-question-circle'}`}
                                    style={{ 
                                        fontSize: '1.5rem',
                                        color: modalAction?.type === 'delete' ? '#dc2626' : '#d97706'
                                    }}
                                ></i>
                            </div>
                            <h5 className="mb-2 fw-medium" style={{ color: '#1e293b' }}>
                                {modalAction?.type === 'delete' ? 'Excluir Template' : 
                                 modalAction?.type === 'desativar' ? 'Desativar Template' : 'Ativar Template'}
                            </h5>
                            <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>
                                {modalAction?.type === 'delete' 
                                    ? `Tem certeza que deseja excluir "${modalAction?.nome}"? Esta ação não pode ser desfeita.`
                                    : modalAction?.type === 'desativar'
                                    ? `Deseja desativar o template "${modalAction?.nome}"?`
                                    : `Deseja ativar o template "${modalAction?.nome}"?`
                                }
                            </p>
                        </div>
                        <div className="d-flex gap-3">
                            <button
                                className="btn flex-fill py-2 border-0"
                                style={{
                                    backgroundColor: '#f8fafc',
                                    color: '#64748b',
                                    borderRadius: '8px',
                                    fontWeight: '500'
                                }}
                                onClick={() => setShowModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn flex-fill py-2 border-0"
                                style={{
                                    backgroundColor: modalAction?.type === 'delete' ? '#dc2626' : 
                                                   modalAction?.type === 'desativar' ? '#d97706' : '#16a34a',
                                    color: 'white',
                                    borderRadius: '8px',
                                    fontWeight: '500'
                                }}
                                onClick={handleConfirm}
                            >
                                {modalAction?.type === 'delete' ? 'Excluir' : 
                                 modalAction?.type === 'desativar' ? 'Desativar' : 'Ativar'}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default Composicoes;
